import { Attachment, PersistedAttachment } from "@/types/attachment";
import { assertNever } from "./assertNever";
import { dataUrlToFile } from "./file";
import { getFileBase64 } from "./getFileBase64";

/**
 * 将持久化的附件数据转换为运行时的 Attachment 格式
 */
export async function persistedToAttachment(
  persisted: PersistedAttachment
): Promise<Attachment> {
  if (persisted.type === "url") {
    return {
      id: persisted.id,
      type: "url",
      url: persisted.url,
    };
  } else if (persisted.type === "file") {
    const file =
      persisted.file ||
      (await dataUrlToFile(
        persisted.dataUrl,
        persisted.name,
        persisted.mimeType,
        persisted.lastModified
      ));

    // 如果是视频且之前是 blob URL（或者重新恢复），我们需要为新 session 生成新的 blob URL
    let preview = persisted.dataUrl;
    if (persisted.mimeType.startsWith("video/")) {
      preview = URL.createObjectURL(file);
    }

    return {
      id: persisted.id,
      type: "file",
      preview,
      file,
    };
  } else {
    return assertNever(persisted);
  }
}

/**
 * 将运行时的 Attachment 转换为可持久化的格式
 */
export async function attachmentToPersisted(
  attachment: Attachment
): Promise<PersistedAttachment> {
  if (attachment.type === "url") {
    return {
      id: attachment.id,
      type: "url",
      url: attachment.url,
    };
  } else if (attachment.type === "file") {
    // 确保有可序列化的预览或原始文件
    let dataUrl = attachment.preview || "";
    const file = attachment.file;

    const isVideo = attachment.file.type.startsWith("video/");

    if (isVideo) {
      // 视频不存储巨大的 base64，直接把 File 对象存入 IndexedDB
      // dataUrl 设为空或占位符，因为 blob: URL 在新 session 会失效
      dataUrl = "";
    } else if (dataUrl.startsWith("blob:") && attachment.file) {
      // 图片如果当前是 blob，则持久化时转回 base64
      dataUrl = await getFileBase64(attachment.file);
    } else if (!dataUrl && attachment.file) {
      dataUrl = await getFileBase64(attachment.file);
    }

    return {
      id: attachment.id,
      type: "file",
      name: attachment.file.name,
      mimeType: attachment.file.type || "",
      lastModified: attachment.file.lastModified || Date.now(),
      dataUrl,
      file,
    };
  } else {
    return assertNever(attachment);
  }
}

/**
 * 批量转换：持久化附件 → 运行时附件
 */
export async function persistedToAttachments(
  persisted: PersistedAttachment[]
): Promise<Attachment[]> {
  return Promise.all(persisted.map((item) => persistedToAttachment(item)));
}

/**
 * 批量转换：运行时附件 → 持久化附件
 */
export async function attachmentsToPersisted(
  attachments: Attachment[]
): Promise<PersistedAttachment[]> {
  return Promise.all(
    attachments.map((attachment) => attachmentToPersisted(attachment))
  );
}
